/** Post-issue: attachments + invoice + delivery confirm. */
import { useState } from "react";
import {
  uploadPurchaseOrderAttachment,
  type MfgPoAttachmentKind,
  type MfgPurchaseOrder,
} from "@/lib/manufacturing/purchaseOrderClient";
import { PurchaseOrderDeliveryConfirm } from "./PurchaseOrderDeliveryConfirm";
import { PurchaseOrderFinalRecord } from "./PurchaseOrderFinalRecord";
import { PurchaseOrderInvoiceSection } from "./PurchaseOrderInvoiceSection";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { isStoredIssuedPurchaseOrder } from "@/lib/manufacturing/issuedPurchaseOrderFile";

export function PurchaseOrderAttachmentsReceive(props: {
  order: MfgPurchaseOrder;
  enabled: boolean;
  onUpdated: (order: MfgPurchaseOrder) => void;
  onReload: () => void;
  onToast: (msg: string, ok: boolean) => void;
  defaultInvoiceCcEmails?: string[];
}) {
  const { order } = props;
  const [busy, setBusy] = useState(false);

  async function upload(kind: MfgPoAttachmentKind, file: File | null) {
    if (!file) return;
    setBusy(true);
    const res = await uploadPurchaseOrderAttachment({
      po_id: order.id,
      kind,
      file,
    });
    setBusy(false);
    if (!res.ok) {
      props.onToast(res.error, false);
      return;
    }
    props.onToast(`Uploaded ${kind.replace(/_/g, " ")}`, true);
    props.onReload();
  }

  return (
    <div className="mt-4 space-y-4 border-t border-gray-100 pt-3 dark:border-zinc-800">
      <PurchaseOrderFinalRecord order={order} />
      <div>
        <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">Attachments</div>
        <p className="text-[11px] text-gray-500">
          Upload the signed PO, vendor confirmation, or shipping invoice.
        </p>
        <div className="mt-2 flex flex-wrap gap-3">
          {(
            [
              ["signed_po", "Signed PO"],
              ["confirmation", "Confirmation"],
              ["shipping_invoice", "Shipping invoice"],
            ] as const
          ).map(([kind, label]) => (
            <label key={kind} className="text-[11px]">
              <span className="font-medium">{label}</span>
              <input
                type="file"
                accept="application/pdf,image/*"
                className={`${inputClass} mt-1 block text-xs`}
                disabled={!props.enabled || busy || order.status === "draft"}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  void upload(kind, f);
                  e.target.value = "";
                }}
              />
            </label>
          ))}
        </div>
        {(order.attachments ?? []).length > 0 ? (
          <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-zinc-400">
            {(order.attachments ?? []).map((a) => (
              <li key={a.id}>
                {isStoredIssuedPurchaseOrder(a)
                  ? "Issued PO"
                  : `${a.kind.replace(/_/g, " ")} — ${a.original_filename || a.storage_path}`}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <PurchaseOrderInvoiceSection
        order={order}
        enabled={props.enabled}
        onUpdated={props.onUpdated}
        onToast={props.onToast}
        defaultInvoiceCcEmails={props.defaultInvoiceCcEmails ?? []}
      />

      <PurchaseOrderDeliveryConfirm
        order={order}
        enabled={props.enabled}
        onUpdated={props.onUpdated}
        onReload={props.onReload}
        onToast={props.onToast}
      />
    </div>
  );
}
